import { formatDate } from '@angular/common';
import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Subject } from 'rxjs';
import {
  FeesData, OtherMetaData, SQWebProject, GetUserPreferencesResponse,
  IProjectHierarchyItem,
  SQWebProjectContainer,
  OpenEstimateResponse,
  CollaborationHistoryData,
  AccessRequestModel,
  EstimateData,
  CostReviewBaseAttributes,
  GetUserDataResponse,
  CollaborationState,
  CostReviewStatus,
  UserNotificationItem,
  CostReviewDatabaseRecord,
  EstimateStatus,
  CollaboratorDetails,
  CollaboratorPreferences,
  TypicalOfSystemHelper,
  SalesOfficeLaborRates,
  OverTimeRates,
  TaskBasedLaborSettings,
  MetaData,
  LaborMetaData,
  MiscMetaData,
  EstimateMetaData,
  CollaborationData,
  LaborConstants,
  TaskBasedLaborConstants
} from '../models/dtos'
import { CostReviewParameters } from '../models/common/CostReviewParameter';
import { CONSTANTS } from '../Constants/Constants';
import { EstimateSearchDropDownDataResponse } from '../models/EstimateSearchDropDownDataResponse';
import { ReportHeaderModel } from '../models/reports/ReportHeaderModel';
import { TypeDropdown } from '../models/TypeDropdownResponse';
import { ReviewDetailTabsService } from 'review-detail-tabs';
import { EstimateNameDescriptionData } from '../models/sol-estimate-table/EstimateNameDescriptionData';
import { UserProfileService } from './common/user-profile.service';

@Injectable({
  providedIn: 'root'
})
export class CommonDataService {

  // ==========================================================================================
  // #region State
  //
  // All estimate-scoped data lives inside a single object: `openEstimate`. External callers
  // MUST NOT reach into that object directly. Writes go through the atomic publishers
  // (`publishOpenEstimate`, `publishProjectContainer`) and the narrow patch methods
  // (`patchEstimate`, `patchLaborMetaData`, `patchMiscMetaData`, `patchEstimateMetaData`,
  // `patchOtherMetaData`, `patchProject`). None of the patch methods writes into a subtree
  // that another patch method can later clobber, so the previous "write order" rule no
  // longer exists.
  // ==========================================================================================

  /** Single source of truth for all estimate-scoped data. Null while no estimate is open. */
  private openEstimate: OpenEstimateResponse = null;
  private estimateSession = 0;

  // --- Estimate-scoped caches that the open estimate response does not carry ---
  /**
   * Pristine, server-computed labor rates captured once per estimate open.
   * Unlike the labor metadata (restored from the persisted project blob, and therefore already
   * containing any user overrides), this is always the freshly recalculated result of
   * `MetaData/GetAllRates`. It is the baseline used to restore overtime rates when a user
   * turns the "override calculation" option off.
   */
  private _defaultLaborRates: SalesOfficeLaborRates = null;
  private _laborConstants: LaborConstants = null;
  private _taskBasedLaborConstants: TaskBasedLaborConstants = null;
  private _feesData: FeesData;
  // To-Do: remove this as part of cleanup and use getSQWebProject() directly where needed
  private _projectHierarchyItems: Record<string, IProjectHierarchyItem>;
  private _reportHeaderData: ReportHeaderModel = new ReportHeaderModel();
  private _costReviewSalesPersonName = CONSTANTS.EMPTY_STRING;
  private reviewParameterDetailsForReport: CostReviewParameters[] = [];
  private _isReviewCancelledBySystem: boolean = false;
  private _proposalRestrictionType: 'Restricted' | 'Unrestricted' = null;
  private _proposalAdvancedOptionsAreaList: { id: string, description: string }[] = [];

  // --- User-scoped state (survives closing an estimate) ---
  private preferenceData: GetUserPreferencesResponse = null;
  private userData: GetUserDataResponse = null;
  private notifications: UserNotificationItem[] = [];
  private _userId: string = '';
  private _decimalPrecision: number;
  private _currentApplicationCulture: string = null;

  // --- Application / UI state ---
  globalPPMmode = false;
  private _estimateSearchDropDownData: EstimateSearchDropDownDataResponse;
  private _activeTab: number = 0;
  private _isSaveEnabled = false;
  private _hasJobDetailsException = false;

  // --- Change notifications ---
  private readonly _estimateDataChanged = new BehaviorSubject<EstimateData | null>(null);
  readonly estimateDataChanged$ = this._estimateDataChanged.asObservable();
  //May not be needed
  private readonly _estimateNameAndDescChanged = new BehaviorSubject<EstimateNameDescriptionData | null>(null);
  readonly estimateNameAndDescChanged$ = this._estimateNameAndDescChanged.asObservable();
  private readonly _estimateProjectNameAndDescChanged = new BehaviorSubject<EstimateNameDescriptionData | null>(null);
  readonly estimateProjectNameAndDescChanged$ = this._estimateProjectNameAndDescChanged.asObservable();
  private readonly _otherMetaDataChanged = new BehaviorSubject<OtherMetaData | null>(null);
  readonly otherMetaDataChanged$ = this._otherMetaDataChanged.asObservable();
  private readonly _propertiesGmPercentChanged = new Subject<{ value: number; hasChanged: boolean }>();
  readonly propertiesGmPercentChanged$ = this._propertiesGmPercentChanged.asObservable();
  private readonly _taskBasedLaborSettings = new BehaviorSubject<TaskBasedLaborSettings | null>(null);
  readonly taskBasedLaborSettings$ = this._taskBasedLaborSettings.asObservable();
  private readonly _readOnlyMode = new BehaviorSubject<boolean>(false);
  readonly readOnlyMode$ = this._readOnlyMode.asObservable();
  private readonly _accessRequestPending = new BehaviorSubject<boolean>(false);
  readonly accessRequestPending$ = this._accessRequestPending.asObservable();
  private readonly _accessRequests = new BehaviorSubject<AccessRequestModel[]>([]);
  readonly accessRequests$ = this._accessRequests.asObservable();
  private readonly _collaborationData = new BehaviorSubject<CollaborationData | null>(null);
  readonly collaborationData$ = this._collaborationData.asObservable();
  private readonly _accessRequestDeclineMessage = new BehaviorSubject<string>('');
  readonly accessRequestDeclineMessage$ = this._accessRequestDeclineMessage.asObservable();
  private readonly _isSapSubmitted = new BehaviorSubject<boolean>(false);
  readonly isSapSubmitted$ = this._isSapSubmitted.asObservable();
  private readonly _projectObjectDataAvailable = new BehaviorSubject<boolean>(false);
  readonly projectObjectDataAvailable$ = this._projectObjectDataAvailable.asObservable();
  private _isSyncNeeded = new BehaviorSubject<boolean>(false);
  readonly isSyncNeeded$ = this._isSyncNeeded.asObservable();
  private readonly _lastSyncDate = new BehaviorSubject<Date | null>(null);
  readonly lastSyncDate$ = this._lastSyncDate.asObservable();
  private _isReviewTabVisible = new BehaviorSubject<boolean>(false);
  readonly isReviewTabVisible$ = this._isReviewTabVisible.asObservable();
  private readonly _selectedMaterialIndicatorId = new BehaviorSubject<string | null>(null);
  readonly selectedMaterialIndicatorId$ = this._selectedMaterialIndicatorId.asObservable();
  private readonly colorSymbolsMode = new BehaviorSubject<boolean>(false);
  readonly colorSymbolsMode$ = this.colorSymbolsMode.asObservable();
  private readonly showInputOutputMode = new BehaviorSubject<boolean>(false);
  readonly showInputOutputMode$ = this.showInputOutputMode.asObservable();
  private readonly _isRootNodeHidden = new BehaviorSubject<boolean>(false);
  readonly isRootNodeHidden$ = this._isRootNodeHidden.asObservable();
  private readonly _leftPanelWidth = new BehaviorSubject<number>(CONSTANTS.SPLIT_LIBRARY_WIDTH);
  readonly leftPanelWidth$ = this._leftPanelWidth.asObservable();
  private readonly userPreferences = new BehaviorSubject<GetUserPreferencesResponse>(null);
  readonly userPreferences$ = this.userPreferences.asObservable();
  // private _reportCheckBoxChanged = new BehaviorSubject<ReportCheckboxes>(null);

  private readonly _subsidyChanged = new BehaviorSubject<{ subsidy: number; subsidyMaterial: number } | null>(null);
  readonly subsidyChanged$ = this._subsidyChanged.asObservable();

  // Root-scoped tab-navigation signal: survives destruction of the emitting
  // component (e.g. inline <open-estimate> in side-nav-bar on the Reports tab).
  private readonly _navigateToEstimateTabRequested = new Subject<void>();
  readonly navigateToEstimateTabRequested$ = this._navigateToEstimateTabRequested.asObservable();

  // #endregion


  // ─── App-side read-only source-of-truth channels ─────────────────────────
  //  The app has a single derived read-only flag (`_readOnlyMode`) OR-composed
  //  from three independent input sources: SAP-Submitted, Collab-Open, and a
  //  manual/user-toggle override. Any input can lock; only when ALL three are
  //  false does the app become editable.
  //
  //  The library flag is written by `ReviewDetailComponent` (the sole writer),
  //  which combines this app flag with the cost-review status. The app service
  //  itself does NOT push to the library except on estimate close, where it
  //  resets the library flag defensively.
  private _isCollaborationOpen: boolean = false;
  /** Programmatic / user-toggle override (header toggle, external callers). */
  private _manualReadOnly: boolean = false;

  constructor(
    private readonly reviewDetailsTabs: ReviewDetailTabsService,
    private readonly userProfileService: UserProfileService,
  ) { }

  // #region Private helpers

  private getProjectContainer(): SQWebProjectContainer {
    return this.openEstimate?.estimateData?.projectObject;
  }

  private getMetaData(): MetaData {
    return this.getProjectContainer()?.metaData;
  }


  /**
   * Re-derive the app-level read-only flag from its three source channels.
   * This is the ONLY place `_readOnlyMode` is written for read-only state.
   * `ReviewDetailComponent` subscribes to `readOnlyMode$` and mirrors the
   * effective value into the library (OR-combined with review status).
   */
  private recomputeReadOnly(): void {
    const effective = this._isSapSubmitted.value
      || this._isCollaborationOpen
      || this._manualReadOnly;
    if (this._readOnlyMode.value !== effective) {
      this._readOnlyMode.next(effective);
    }
  }

  /** Dev-only signal that a patch was called before an estimate was open. */
  private warnNoOpenEstimate(op: string): void {
    if (isDevMode()) {
      console.warn(`[CommonDataService] ${op} called before publishOpenEstimate — write dropped.`);
    }
  }

  // #endregion

  // #region Atomic publishers

  /**
   * Publishes the open-estimate API response and makes it the root of the estimate-scoped state.
   * Every field of the response - estimate data, project container, metadata, cost review and
   * collaboration data - becomes the new source of truth for its subject/getter in one shot.
   * Emits a single `estimateDataChanged$` snapshot covering the full response.
   */
  publishOpenEstimate(response: OpenEstimateResponse): void {
    this.estimateSession++;
    if (this.openEstimate?.estimateData?.estimateID !== response.estimateData?.estimateID) {
      this.setCostReviewSalesPersonName(CONSTANTS.EMPTY_STRING);
    }
    this.openEstimate = response;
    this.updateReportHeaderData(response.estimateData);
    this._estimateDataChanged.next(response.estimateData ? structuredClone(response.estimateData) : null);
    this._estimateNameAndDescChanged.next({
      estimateName: response.estimateData?.estimateName ?? CONSTANTS.EMPTY_STRING,
      description: response.estimateData?.description ?? CONSTANTS.EMPTY_STRING
    });
    if (response.estimateData?.estimateStatusId === EstimateStatus.Submitted) {
      this._isSapSubmitted.next(true);
    } else {
      this._isSapSubmitted.next(false);
    }
    const collaborationState = response.collaborationData?.collaborationState ?? CollaborationState.NonCollaborative;
    this._isCollaborationOpen = collaborationState !== CollaborationState.NonCollaborative
      && collaborationState !== CollaborationState.Edit;
    this.recomputeReadOnly();
    this._collaborationData.next(this.openEstimate.collaborationData ?? null);
    const container = response.estimateData?.projectObject;
    if (container) {
      this.emitProjectContainerChanges(container);
    }
  }

  /**
   * Swaps the estimate's `projectObject` in place and fires only the container-scoped
   * subjects (`taskBasedLaborSettings$`, `estimateProjectNameAndDescChanged$`). Does NOT
   * re-emit `estimateDataChanged$` — the open flow already emits it in `publishOpenEstimate`,
   * and subscribers that need the current container should read it lazily via `getEstimateData()`.
   */
  publishProjectContainer(container: SQWebProjectContainer): void {
    if (!this.openEstimate?.estimateData) {
      this.warnNoOpenEstimate('publishProjectContainer');
      return;
    }
    this.openEstimate.estimateData.projectObject = container;
    this.emitProjectContainerChanges(container);
  }

  private emitProjectContainerChanges(container: SQWebProjectContainer): void {
    const taskBasedLabor = container?.metaData?.laborMetaData?.taskBasedLaborSettings ?? null;
    if (this._taskBasedLaborSettings.getValue() !== taskBasedLabor) {
      this._taskBasedLaborSettings.next(taskBasedLabor);
    }
    this._estimateProjectNameAndDescChanged.next({
      estimateName: container?.sqWebProject?.itemName ?? CONSTANTS.EMPTY_STRING,
      description: container?.sqWebProject?.description ?? CONSTANTS.EMPTY_STRING
    });

    if (this._projectObjectDataAvailable.value) {
      this._projectObjectDataAvailable.next(true);
    }
  }

  getEstimateOpenResponse(): OpenEstimateResponse {
    return this.openEstimate;
  }

  getEstimateSession(): number {
    return this.estimateSession;
  }

  /**
   * Resets all estimate-scoped state to initial values.
   * Called when the user closes an estimate/project.
   * Does NOT clear user-level data (preferenceData, userData, preferenceDropDownData).
   */
  resetEstimateState(): void {
    this.estimateSession++;
    this.openEstimate = null;
    this.setCostReviewSalesPersonName(CONSTANTS.EMPTY_STRING);
    this._accessRequests.next([]);
    this._collaborationData.next(null);
    this._accessRequestDeclineMessage.next('');
    this._accessRequestPending.next(false);

    // Caches not carried by the open estimate.
    this._defaultLaborRates = null;
    this._feesData = null;
    this._projectHierarchyItems = null;
    this._isReviewCancelledBySystem = false;
    this._proposalRestrictionType = null;
    this._proposalAdvancedOptionsAreaList = [];
    this._isSaveEnabled = false;
    this._hasJobDetailsException = false;

    this._reportHeaderData = new ReportHeaderModel();
    this.reviewParameterDetailsForReport = [];
    this._estimateDataChanged.next(null);
    this._estimateNameAndDescChanged.next(null);
    this._estimateProjectNameAndDescChanged.next(null);
    this._lastSyncDate.next(null);

    // ── BehaviorSubjects (emit default values) ──────────────────
    // Clear every read-only source channel, then recompute so the app flag
    // settles to false.
    this._isSapSubmitted.next(false);
    this._isCollaborationOpen = false;
    this._manualReadOnly = false;
    this.recomputeReadOnly();
    // Defensive: reset the library flag directly in case ReviewDetailComponent
    // is not currently mounted to push the combined value.
    this.reviewDetailsTabs.setReadOnlyMode(false);

    this._readOnlyMode.next(false);
    this._projectObjectDataAvailable.next(false);
    this._isSapSubmitted.next(false);
    this._isSyncNeeded.next(false);
    this._isReviewTabVisible.next(false);
    this._selectedMaterialIndicatorId.next(null);
    this.colorSymbolsMode.next(false);
    this._subsidyChanged.next(null);
    this.showInputOutputMode.next(false);
    this._otherMetaDataChanged.next(null);
    this._taskBasedLaborSettings.next(null);
  }

  // #endregion

  // #region Estimate header (patch API)

  getEstimateData(): EstimateData {
    return this.openEstimate?.estimateData;
  }

  /**
   * Merges the supplied fields into the open estimate's `estimateData`. Every field is optional;
   * unspecified fields are preserved. Emits `estimateDataChanged$` and, when relevant,
   * `estimateNameAndDescChanged$` / read-only / sap-submitted subjects.
   *
   * `projectObject` is intentionally excluded — swapping the container must go through
   * `publishProjectContainer` so its side effects (task-based labor + project name/desc
   * emissions) fire.
   */
  patchEstimate(patch: Partial<Omit<EstimateData, 'projectObject'>>): void {
    const estimateData = this.openEstimate?.estimateData;
    if (!estimateData) {
      this.warnNoOpenEstimate('patchEstimate');
      return;
    }
    Object.assign(estimateData, patch);

    // Mirror the name into estimateSettings when the container is present.
    if (patch.estimateName !== undefined) {
      const settings = estimateData.projectObject?.metaData?.estimateMetaData?.estimateSettings;
      if (settings) {
        settings.estimateName = estimateData.estimateName;
      }
    }

    this._estimateDataChanged.next(structuredClone(estimateData));

    if (patch.estimateName !== undefined || patch.description !== undefined) {
      this._estimateNameAndDescChanged.next({
        estimateName: estimateData.estimateName ?? CONSTANTS.EMPTY_STRING,
        description: estimateData.description ?? CONSTANTS.EMPTY_STRING
      });
    }

    if (patch.estimateStatusId !== undefined) {
      if (estimateData.estimateStatusId === EstimateStatus.Submitted) {
        this._isSapSubmitted.next(true);
      } else {
        this._isSapSubmitted.next(false);
      }
      this.recomputeReadOnly();
    }
  }

  getEstimateId(): string {
    return this.openEstimate?.estimateData?.estimateID ?? CONSTANTS.DEFAULT_ESTIMATE_ID;
  }

  getEstimateName(): string {
    return this.getMetaData()?.estimateMetaData?.estimateSettings?.estimateName;
  }

  getEstimateCultureId(): number {
    return this.openEstimate?.estimateData?.cultureID ?? CONSTANTS.ONE;
  }

  getBusiness(): string {
    return this.openEstimate?.estimateData?.business ?? CONSTANTS.EMPTY_STRING;
  }

  getEstimateSoldAsSellPrice(): number {
    const estimateMetaData = this.getEstimateMetaData();
    return estimateMetaData?.estimateTab?.soldASSellPrice ?? 0;
  }

  isArchivedEstimate(): boolean {
    return this.openEstimate?.estimateData?.isArchived ?? false;
  }

  get isSapSubmitted(): boolean {
    return this._isSapSubmitted.getValue();
  }

  set isSapSubmitted(value: boolean) {
    this._isSapSubmitted.next(value);
    this.recomputeReadOnly();
  }

  // #endregion

  // #region Project (patch API)

  getSQWebProject() {
    return this.getProjectContainer()?.sqWebProject;
  }

  /**
   * Replaces the open estimate's `SQWebProject` with the supplied full object. Emits
   * `estimateProjectNameAndDescChanged$` when name or description differ from the previous
   * project. No-op with a dev warning when no estimate is open.
   */
  patchProject(project: SQWebProject): void {
    const container = this.getProjectContainer();
    if (!container?.sqWebProject) {
      this.warnNoOpenEstimate('patchProject');
      return;
    }
    // Compare against the last emitted (primitive) snapshot, not container.sqWebProject:
    // callers mutate the live sqWebProject reference before calling patchProject, so by
    // this point container.sqWebProject === project and a reference-based diff would never fire.
    const previous = this._estimateProjectNameAndDescChanged.getValue();
    container.sqWebProject = project;
    const newName = project?.itemName ?? CONSTANTS.EMPTY_STRING;
    const newDescription = project?.description ?? CONSTANTS.EMPTY_STRING;
    if (newName !== (previous?.estimateName ?? CONSTANTS.EMPTY_STRING) || newDescription !== (previous?.description ?? CONSTANTS.EMPTY_STRING)) {
      this._estimateProjectNameAndDescChanged.next({
        estimateName: newName,
        description: newDescription
      });
    }
  }

  getProjectItemNameAndDescription(): EstimateNameDescriptionData {
    const project = this.getSQWebProject();
    return {
      estimateName: project?.itemName ?? CONSTANTS.EMPTY_STRING,
      description: project?.description ?? CONSTANTS.EMPTY_STRING
    };
  }

  getTypicalOfHelpers(): Record<string, TypicalOfSystemHelper> {
    return this.getProjectContainer()?.typicalOfHelperDictionary ?? {};
  }

  setTypicalOfHelpers(values: Record<string, TypicalOfSystemHelper>): void {
    const projectContainer = this.getProjectContainer();
    if (!projectContainer) {
      this.warnNoOpenEstimate('setTypicalOfHelpers');
      return;
    }
    projectContainer.typicalOfHelperDictionary = values;
  }

  // To-Do: remove this as part of cleanup and use getSQWebProject() directly where needed
  getProjectHierarchyItems() {
    return this._projectHierarchyItems ?? {};
  }
  // To-Do: remove this as part of cleanup and use getSQWebProject() directly where needed
  setProjectHierarchyItems(projectHierarchyItems: Record<string, IProjectHierarchyItem>) {
    this._projectHierarchyItems = projectHierarchyItems;
  }

  // #endregion

  // #region Metadata (patch API)

  getEstimateMetaData() {
    return this.getMetaData()?.estimateMetaData;
  }

  getLaborMetaData() {
    return this.getMetaData()?.laborMetaData;
  }

  getMiscMetaData() {
    return this.getMetaData()?.miscMetaData;
  }

  getOtherMetaData() {
    return this.getMetaData()?.otherMetaData;
  }

  /**
   * Replaces the open estimate's `laborMetaData` with the supplied full object and re-emits
   * `taskBasedLaborSettings$` when the nested settings reference changed. No-op with a dev
   * warning when no estimate is open.
   */
  patchLaborMetaData(laborMetaData: LaborMetaData): void {
    const meta = this.getMetaData();
    if (!meta) {
      this.warnNoOpenEstimate('patchLaborMetaData');
      return;
    }
    meta.laborMetaData = laborMetaData;
    const taskBased = laborMetaData?.taskBasedLaborSettings ?? null;
    if (this._taskBasedLaborSettings.getValue() !== taskBased) {
      this._taskBasedLaborSettings.next(taskBased);
    }
  }

  /**
   * Replaces the open estimate's `miscMetaData` with the supplied full object. No-op with a
   * dev warning when no estimate is open.
   */
  patchMiscMetaData(miscMetaData: MiscMetaData): void {
    const meta = this.getMetaData();
    if (!meta) {
      this.warnNoOpenEstimate('patchMiscMetaData');
      return;
    }
    meta.miscMetaData = miscMetaData;
  }

  /**
   * Replaces the open estimate's `estimateMetaData` with the supplied full object. No-op with
   * a dev warning when no estimate is open.
   */
  patchEstimateMetaData(estimateMetaData: EstimateMetaData): void {
    const meta = this.getMetaData();
    if (!meta) {
      this.warnNoOpenEstimate('patchEstimateMetaData');
      return;
    }
    meta.estimateMetaData = estimateMetaData;
  }

  /**
   * Replaces the open estimate's `otherMetaData` with the supplied full object and fires
   * `otherMetaDataChanged$` unless `options.silent` is true.
   *
   * Dashboard flows (create-opportunity, customer-information) that share only `otherMetaData`
   * and run without an open estimate still fire the change notification; the write into the
   * metadata tree is skipped in that case.
   */
  patchOtherMetaData(otherMetaData: OtherMetaData, options: { silent?: boolean } = {}): void {
    const meta = this.getMetaData();
    if (meta) {
      meta.otherMetaData = otherMetaData;
    }
    if (!options.silent) {
      this._otherMetaDataChanged.next(otherMetaData ? structuredClone(otherMetaData) : null);
    }
  }

  notifySubsidyChanged(): void {
    const estimateTab = this.getEstimateMetaData()?.estimateTab;
    this._subsidyChanged.next(estimateTab ? {
      subsidy: estimateTab?.subsidy ?? CONSTANTS.ZERO,
      subsidyMaterial: estimateTab?.subsidyMaterial ?? CONSTANTS.ZERO
    } : null);
  }

  getTaskBasedLaborSettings(): TaskBasedLaborSettings | null {
    return this.getLaborMetaData()?.taskBasedLaborSettings ?? null;
  }

  /**
   * Writes task-based labor settings (incl. job details) onto the labor metadata held by the
   * open estimate and emits on `taskBasedLaborSettings$`. The reference check breaks the
   * app <-> library echo loop.
   */
  setTaskBasedLaborSettings(taskBasedLaborSettings: TaskBasedLaborSettings | null): void {
    if (this._taskBasedLaborSettings.getValue() === taskBasedLaborSettings) {
      return;
    }
    const laborMetaData = this.getLaborMetaData();
    if (laborMetaData) {
      laborMetaData.taskBasedLaborSettings = taskBasedLaborSettings;
    }
    this._taskBasedLaborSettings.next(taskBasedLaborSettings);
  }

  setDefaultLaborRates(laborRates: SalesOfficeLaborRates): void {
    this._defaultLaborRates = laborRates ? structuredClone(laborRates) : null;
  }

  get laborConstants(): LaborConstants | null {
    return this._laborConstants;
  }

  set laborConstants(value: LaborConstants | null) {
    this._laborConstants = value ? structuredClone(value) : null;
  }

  get taskBasedLaborConstants(): TaskBasedLaborConstants | null {
    return this._taskBasedLaborConstants;
  }

  set taskBasedLaborConstants(value: TaskBasedLaborConstants | null) {
    this._taskBasedLaborConstants = value ? structuredClone(value) : null;
  }

  /**
   * Server-computed overtime rates for a sales office, before any user override.
   * Returns null when the defaults were never captured (e.g. read-only/SAP-submitted
   * estimates, where the rates fetch is skipped) or the office is unknown.
   */
  getDefaultOvertimeRates(salesOfficeId: number): OverTimeRates | null {
    return this._defaultLaborRates?.find(rate => rate.salesOfficeId === salesOfficeId)?.stdOverTimeRates ?? null;
  }

  getFeesData() {
    return this._feesData;
  }

  setFeesData(feesData: FeesData) {
    this._feesData = feesData;
  }

  // #endregion

  // #region Reports

  getReportHeaderData(): ReportHeaderModel {
    this._reportHeaderData.salesPersonName = this.resolveReportSalesPersonName();
    return this._reportHeaderData;
  }

  setCostReviewSalesPersonName(salesPersonName: string): void {
    this._costReviewSalesPersonName = salesPersonName ?? CONSTANTS.EMPTY_STRING;
  }

  private resolveReportSalesPersonName(): string {
    if (this.isCollaborationEstimate()) {
      const primaryUserName = this.getCollaboratorData().find(collaborator => collaborator.isPrimaryUser)?.name ?? CONSTANTS.EMPTY_STRING;
      const costReviewData = this.getCostReviewData();
      if (costReviewData?.status === CostReviewStatus.InProcess || costReviewData?.status === CostReviewStatus.Approved) {
        return this._costReviewSalesPersonName || primaryUserName;
      }
      return primaryUserName;
    }

    return this.preferenceData?.preferences?.salesPersonName || this.userProfileService.userInfo?.name || CONSTANTS.EMPTY_STRING;
  }

  /** Keeps report header fields current; collaborative Sales Person comes from the estimate record. */
  private updateReportHeaderData(estimateData: EstimateData): void {
    this._reportHeaderData.estimateId = this.getEstimateId();
    this._reportHeaderData.estimateName = estimateData.estimateName;
    this._reportHeaderData.salesPersonName = this.resolveReportSalesPersonName();
    this._reportHeaderData.jobStart = estimateData.startDate ? formatDate(estimateData.startDate, 'd-MMM-yyyy', 'en-US') : '';
    this._reportHeaderData.jobEnd = estimateData.endDate ? formatDate(estimateData.endDate, 'd-MMM-yyyy', 'en-US') : '';
  }

  updateReportDates(startDate: Date | null, endDate: Date | null): void {
    this._reportHeaderData.jobStart = startDate ? formatDate(startDate, 'd-MMM-yyyy', 'en-US') : '';
    this._reportHeaderData.jobEnd = endDate ? formatDate(endDate, 'd-MMM-yyyy', 'en-US') : '';
  }

  get projectStartDateEndDate(): { startDate: Date | null; endDate: Date | null } {
    return {
      startDate: this.openEstimate?.estimateData.startDate ?? null,
      endDate: this.openEstimate?.estimateData.endDate ?? null,
    };
  }

  // #endregion


  // #region Cost review

  getCostReviewData(): CostReviewDatabaseRecord {
    return this.openEstimate?.costReviewDatabaseRecord;
  }

  /** Replaces the cost review record of the open estimate. No-op while no estimate is open. */
  setCostReviewData(costReviewData: CostReviewDatabaseRecord): void {
    if (this.openEstimate) {
      this.openEstimate.costReviewDatabaseRecord = costReviewData;
    }
  }

  notifyPropertiesGmPercentChanged(value: number, hasChanged: boolean): void {
    if (Number.isFinite(value)) {
      this._propertiesGmPercentChanged.next({ value, hasChanged });
    }
  }

  getReviewObject(): CostReviewBaseAttributes | undefined {
    return this.getEstimateOpenResponse()?.costReviewDatabaseRecord?.reviewObject;
  }

  get getIsReviewCancelledBySystem(): boolean {
    return this._isReviewCancelledBySystem;
  }

  set setIsReviewCancelledBySystem(isReviewCancelledBySystem: boolean) {
    this._isReviewCancelledBySystem = isReviewCancelledBySystem;
  }

  setReviewTabVisibility(value: boolean): void {
    this._isReviewTabVisible.next(value);
  }

  getReviewTabVisibility(): boolean {
    return this._isReviewTabVisible.value;
  }


  setReviewParameterResponse(reviewParameterDetails: CostReviewParameters[]): void {
    this.reviewParameterDetailsForReport = reviewParameterDetails;
    this.reviewDetailsTabs.setReviewParameterData(reviewParameterDetails);
  }

  getReviewParameterResponse(): CostReviewParameters[] {
    return this.reviewParameterDetailsForReport;
  }

  // #endregion

  // #region Collaboration

  /**
   * Applies the collaboration data of the open estimate. It does not store anything - the data
   * already lives on the open estimate - it only switches read-only mode on/off.
   * WRITE ORDER: level 2 - called by `setOpenEstimate`.
   */
  setCollaborationData(collaborationData: CollaborationData): void {
    // Update only the collab-open source channel; recomputeReadOnly derives the
    // effective app-level flag. This cannot clear an SAP-Submitted or manual
    // read-only lock — they live in their own channels.
    if (this.openEstimate) {
      this.openEstimate.collaborationData = collaborationData;
    }
    const collaborationState = collaborationData?.collaborationState ?? CollaborationState.NonCollaborative;
    this._isCollaborationOpen = collaborationState !== CollaborationState.NonCollaborative
      && collaborationState !== CollaborationState.Edit;
    this.recomputeReadOnly();
    this._collaborationData.next(this.openEstimate?.collaborationData ?? null);
  }

  isCollaborationEstimate(): boolean {
    return this.openEstimate ? this.openEstimate?.collaborationData?.collaborationState !== CollaborationState.NonCollaborative : false;
  }

  getCollaborationState(): CollaborationState {
    return this.openEstimate ? this.openEstimate?.collaborationData?.collaborationState : CollaborationState.NonCollaborative;
  }

  /**
   * Updates the collaboration state of the open estimate in place and syncs read-only mode.
   * No-op while no estimate is open.
   */
  setCollaborationState(state: CollaborationState): void {
    if (this.openEstimate?.collaborationData) {
      this.openEstimate.collaborationData.collaborationState = state;
      this._isCollaborationOpen = state !== CollaborationState.NonCollaborative
        && state !== CollaborationState.Edit;
      this.recomputeReadOnly();
      this._collaborationData.next(this.openEstimate.collaborationData);
    }
  }

  getCollaborationHistory(): CollaborationHistoryData[] {
    return this.openEstimate?.collaborationData?.collaborationHistoryDataList ?? [];
  }

  getCollaboratorData(): CollaboratorDetails[] {
    return this.openEstimate?.collaborationData?.collaboratorDataList ?? [];
  }

  /** Updates the collaborator list of the open estimate in place. No-op while no estimate is open. */
  setCollaboratorDataList(list: CollaboratorDetails[]): void {
    if (this.openEstimate?.collaborationData) {
      this.openEstimate.collaborationData.collaboratorDataList = list;
      this._collaborationData.next(this.openEstimate.collaborationData);
    }
  }

  setCollaborationPollingSnapshot(
    collaborators: CollaboratorDetails[],
    history: CollaborationHistoryData[],
    state: CollaborationState
  ): void {
    if (!this.openEstimate?.collaborationData) {
      return;
    }
    this.openEstimate.collaborationData.collaboratorDataList = collaborators;
    this.openEstimate.collaborationData.collaborationHistoryDataList = history;
    this.openEstimate.collaborationData.collaborationState = state;
    this._isCollaborationOpen = state !== CollaborationState.NonCollaborative
      && state !== CollaborationState.Edit;
    this.recomputeReadOnly();
    this._collaborationData.next(this.openEstimate.collaborationData);
  }

  setCollaborationHistory(mappedHistory: CollaborationHistoryData[]): void {
    if (!mappedHistory?.length) {
      return;
    }
    if (this.openEstimate?.collaborationData) {
      this.openEstimate.collaborationData.collaborationHistoryDataList = mappedHistory;
      this._collaborationData.next(this.openEstimate.collaborationData);
    }
  }

  setAccessRequestDeclineMessage(message: string): void {
    this._accessRequestDeclineMessage.next(message);
  }

  setAccessRequests(requests: AccessRequestModel[]): void {
    this._accessRequests.next(requests);
  }

  setAccessRequestPending(isPending: boolean): void {
    if (isPending) {
      this._accessRequestDeclineMessage.next('');
    }
    if (this._accessRequestPending.value !== isPending) {
      this._accessRequestPending.next(isPending);
    }
  }

  getAccessRequestPending(): boolean {
    return this._accessRequestPending.value;
  }

  // #endregion

  // #region CRM / opportunity

  getOpportunityRelationshipData() {
    return this.openEstimate ? this.openEstimate.opportunityRelationshipData : null;
  }

  get ProposalRestrictionType(): 'Restricted' | 'Unrestricted' {
    return this._proposalRestrictionType;
  }

  set ProposalRestrictionType(value: 'Restricted' | 'Unrestricted') {
    this._proposalRestrictionType = value;
  }

  get proposalAdvancedOptionsAreaList(): { id: string, description: string }[] {
    return this._proposalAdvancedOptionsAreaList;
  }

  set proposalAdvancedOptionsAreaList(value: { id: string, description: string }[]) {
    this._proposalAdvancedOptionsAreaList = value;
  }

  // #endregion

  // #region User data & preferences (not estimate scoped)

  getPreferenceData(): GetUserPreferencesResponse {
    return this.preferenceData;
  }

  setPreferenceData(preferenceData: GetUserPreferencesResponse): void {
    this.preferenceData = preferenceData;
    this.reviewDetailsTabs.setUserCultureString(preferenceData?.preferences?.cultureString);
    this._reportHeaderData.salesPersonName = this.resolveReportSalesPersonName();
  }

  setCollaboratorDetails(collaboratorDetails: CollaboratorPreferences): void {
    if (this.preferenceData) {
      this.preferenceData.collaboratorPreferences.collaborators = collaboratorDetails.collaborators;
      this.preferenceData.collaboratorPreferences.availableCollaborators = collaboratorDetails.availableCollaborators;
    }
  }

  getAllCollaboratorPreferences() {
    return this.preferenceData && this.preferenceData?.collaboratorPreferences ? this.preferenceData.collaboratorPreferences : null;
  }

  getPreferenceCollaborators() {
    return this.preferenceData && this.preferenceData?.collaboratorPreferences?.collaborators ? this.preferenceData.collaboratorPreferences.collaborators : [];
  }

  getPreferenceCostReviewers() {
    return this.preferenceData && this.preferenceData?.costReviewerPreferences?.crReviewers ? this.preferenceData.costReviewerPreferences.crReviewers : [];
  }

  getUserLanguagePreference(): string {
    return this.preferenceData?.preferences?.cultureString;
  }

  getUserData(): GetUserDataResponse {
    return this.userData;
  }

  setUserData(userData: GetUserDataResponse): void {
    this.userData = userData;
  }

  getUserId(): string {
    return this._userId;
  }

  setUserId(userId: string): void {
    this._userId = userId;
  }

  getNotifications(): UserNotificationItem[] {
    return this.notifications;
  }

  setNotifications(notifications: UserNotificationItem[]): void {
    this.notifications = notifications;
  }

  setDecimalPrecision(decimalPrecision: number): void {
    this._decimalPrecision = decimalPrecision;
    this.reviewDetailsTabs.setDecimalPrecision(decimalPrecision);
  }

  getDecimalPrecision(): number {
    return this._decimalPrecision ?? CONSTANTS.TWO;
  }

  getCurrentApplicationCulture(): string {
    return this._currentApplicationCulture ?? CONSTANTS.LANGUAGE_EN_US;
  }

  setCurrentApplicationCulture(culture: string | null | undefined): void {
    this._currentApplicationCulture = culture ?? CONSTANTS.LANGUAGE_EN_US;
  }

  // #endregion

  // #region Application / UI state (not estimate scoped)

  /**
   * Manual / user-driven read-only writer (header toggle, external callers).
   * Writes the "manual" channel only. SAP-Submitted and Collab-Open remain
   * hard floors — passing `false` here cannot clear them.
   */
  enableReadOnlyMode(value: boolean) {
    this._manualReadOnly = value;
    this.recomputeReadOnly();
  }

  isReadOnlyMode(): boolean {
    return this._readOnlyMode.getValue();
  }

  setProjectObjectDataAvailable(value: boolean): void {
    this._projectObjectDataAvailable.next(value);
  }

  getProjectObjectDataAvailable(): boolean {
    return this._projectObjectDataAvailable.getValue();
  }

  setIsSyncNeeded(value: boolean): void {
    this._isSyncNeeded.next(value);
  }

  getIsSyncNeeded(): boolean {
    return this._isSyncNeeded.value;
  }

  setLastSyncDate(lastSyncDate: Date | null): void {
    this._lastSyncDate.next(lastSyncDate);
  }

  getLastSyncDate(): Date | null {
    return this._lastSyncDate.getValue();
  }

  get isSaveEnabled(): boolean {
    return this._isSaveEnabled;
  }

  set isSaveEnabled(value: boolean) {
    this._isSaveEnabled = value;
  }

  setActiveTab(selectedTab: number): void {
    this._activeTab = selectedTab;
  }

  getActiveTab(): number {
    return this._activeTab;
  }

  requestNavigateToEstimateTab(): void {
    this._navigateToEstimateTabRequested.next();
  }

  getLeftPanelWidth(): number {
    return this._leftPanelWidth.getValue();
  }

  setLeftPanelWidth(value: number): void {
    this._leftPanelWidth.next(value);
  }

  setSelectedMaterialIndicatorId(id: string | null) {
    this._selectedMaterialIndicatorId.next(id);
  }

  setColorSymbolsMode(value: boolean) {
    this.colorSymbolsMode.next(value);
  }

  setShowInputOutputMode(value: boolean) {
    this.showInputOutputMode.next(value);
  }

  setRootNodeHidden(value: boolean) {
    this._isRootNodeHidden.next(value);
  }

  getRootNodeHidden(): boolean {
    return this._isRootNodeHidden.getValue();
  }

  setEstimateSearchDropdownData(dropdownData: EstimateSearchDropDownDataResponse): void {
    this._estimateSearchDropDownData = dropdownData;
  }

  getEstimateTypeDropDownData(): TypeDropdown[] {
    return this._estimateSearchDropDownData ? this._estimateSearchDropDownData.type : [];
  }

  //TODO: Remove this while job details refactoring
  get jobDetailsExceptionVisible(): boolean {
    return this._hasJobDetailsException;
  }
  set jobDetailsExceptionVisible(value: boolean) {
    this._hasJobDetailsException = value;
  }
  // #endregion
}
